import React, { useState, useEffect, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity
} from 'react-native';
import moment from 'moment';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import DataTable from "react-data-table-component";
import { getStorage, setStorage } from '../storage';
import Data from './Data';
import { useScale, ScaleFn, TablePagination , useDarkMode} from './common';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

const sensorColumns = [
    {
        id: 1,
        name: "no.",
        selector: (row: any, index: any) => index + 1,
        grow: 0
    },
    {
        id: 2,
        name: "이름",
        selector: (row: any) => row.NAME,
    },
    {
        id: 3,
        name: '아이디',
        selector: (row: any) => row.SENSOR_ID,
    },
    {
        id: 4,
        name: '구역',
        selector: (row: any) => row.AREA,
    },
    {
        id: 5,
        name: '사용일수',
        selector: (row: any) => row.USE_DAYS,
    },
    {
        id: 6,
        name: "사용시간",
        selector: (row: any) => row.USE_HOURS != null ? Number(row.USE_HOURS).toLocaleString() : '-',
    },
    {
        id: 7,
        name: '센서 교체 횟수',
        selector: (row: any) => row.SENSOR_COUNT != null ? Number(row.SENSOR_COUNT).toLocaleString() : '-',
    },
];

export default function Sensor({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [list, setList] = useState<any>([]);
    const [filteredList, setFilteredList] = useState<any>([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const _totalPages = Math.max(1, Math.ceil(((filteredList) || []).length / perPage));
    const _curPage = Math.min(Math.max(1, page), _totalPages);
    const pageData = ((filteredList) || []).slice((_curPage - 1) * perPage, _curPage * perPage);
    const [searchText, setSearchText] = useState('');
    const [admin, setAdmin] = useState('1');
    const [selectedMonth, setSelectedMonth] = useState(moment().format("YYYY-MM"));
    const { dark, setDark } = useDarkMode();

    useEffect(() => {
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    const prevMonth = () => setSelectedMonth(moment(selectedMonth).subtract(1, 'month').format('YYYY-MM'));
    const nextMonth = () => setSelectedMonth(moment(selectedMonth).add(1, 'month').format('YYYY-MM'));
    const isCurrentMonth = selectedMonth === moment().format('YYYY-MM');

    const loadData = async (month: string) => {
        const userInfo = await getStorage('AutoLogin');
        const adminValue = userInfo?.admin;
        setAdmin(adminValue);

        Data.fetchData('sensor/SensorUse', { month }, (result: any, count: any) => {
            if (count > 0) {
                const sortedList = result.sort((a: any, b: any) => {
                    if (a.AREA === '로밍') return 1;
                    if (b.AREA === '로밍') return -1;
                    return a.AREA.localeCompare(b.AREA, 'ko');
                });
                setList(sortedList);
            } else {
                setList([]);
            }
        }, true);
    };

    useEffect(() => {
        loadData(selectedMonth);
    }, [isFocused, selectedMonth]);

    useEffect(() => {
        if (searchText === '') setFilteredList(list);
        else {
            setFilteredList(
                list.filter((item: any) => item.NAME.includes(searchText))
            );
        }
    }, [searchText, list]);

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* ─── 헤더 ─── */}
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>센서 사용량</Text>
                        {filteredList?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{filteredList.length}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        {/* 월 선택 */}
                        <View style={[styles.monthSelector, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                            <TouchableOpacity onPress={prevMonth} style={styles.monthArrow}>
                                <AntDesign name="left" size={s(14)} color={T.textSub} />
                            </TouchableOpacity>
                            <Text style={{ fontSize: s(14), fontWeight: '600', color: T.text, minWidth: s(80), textAlign: 'center' }}>
                                {moment(selectedMonth).format('YYYY년 MM월')}
                            </Text>
                            <TouchableOpacity onPress={nextMonth} style={[styles.monthArrow, isCurrentMonth && { opacity: 0.3 }]} disabled={isCurrentMonth}>
                                <AntDesign name="right" size={s(14)} color={T.textSub} />
                            </TouchableOpacity>
                        </View>
                        {/* 검색 */}
                        <View style={[styles.searchContainer, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                            <Ionicons name="search-sharp" size={s(18)} color={T.muted} style={{ marginRight: s(8) }} />
                            <TextInput
                                style={[styles.searchBox, { color: T.text }]}
                                placeholder="이름 검색..."
                                placeholderTextColor={T.muted}
                                value={searchText}
                                onChangeText={(text) => setSearchText(text)}
                            />
                            {searchText !== '' && (
                                <TouchableOpacity onPress={() => setSearchText('')} style={{ padding: s(2) }}>
                                    <AntDesign name="closecircle" size={s(14)} color={T.muted} />
                                </TouchableOpacity>
                            )}
                        </View>
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { navigation.navigate('Setting') }}
                        >
                            <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>
                {/* ─── 테이블 ─── */}
                <DataTable
                    columns={sensorColumns}
                    data={pageData}
                    customStyles={tableStyles}
                    noDataComponent={
                        <View style={{ padding: s(40), alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: s(16), color: T.muted, textAlign: 'center' }}>
                                표시할 데이터가 없습니다.{'\n'}
                                월이 시작된 경우, 당일 데이터는 집계 이후 표시됩니다.
                            </Text>
                        </View>
                    }
                />
                {(filteredList || []).length > 0 && (
                    <TablePagination
                        rowCount={(filteredList || []).length}
                        perPage={perPage}
                        page={_curPage}
                        onChangePage={setPage}
                        onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
                        T={T}
                        s={s}
                        accent={S.accent}
                    />
                )}
            </View>
        </View>
    );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: {
        flex: 1,
        padding: s(24),
    },
    dataTable: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(10),
    },
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: s(6),
        paddingBottom: s(12),
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        height: s(36),
        borderWidth: 1,
        borderRadius: s(10),
        paddingHorizontal: s(12),
        width: s(200),
    },
    searchBox: {
        flex: 1,
        fontSize: s(14),
        outlineStyle: 'none',
    },
    monthSelector: {
        flexDirection: 'row',
        alignItems: 'center',
        height: s(36),
        borderWidth: 1,
        borderRadius: s(10),
        paddingHorizontal: s(4),
    },
    monthArrow: {
        width: s(30),
        height: s(30),
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconBtn: {
        width: s(34),
        height: s(34),
        borderRadius: s(10),
        justifyContent: 'center',
        alignItems: 'center',
    },
});
